import { FC, useMemo, useState } from 'react';

import { useRecoilValue, useSetRecoilState } from 'recoil';

import DateFilterSearcher from '@service/bugo/components/DateFilterSearcher';
import StatPageTableHeaderBox from '@service/bugo/components/StatPageTableHeaderBox';
import {
  FlowerStatDateType,
  FlowerStatItem,
} from '@shared/api/flowerStat/flowerStat.interface';
import { Button } from '@shared/components/Button';
import CheckboxFilter, { CheckBoxFilter } from '@shared/components/CheckboxFilter';
import { TextField } from '@shared/components/TextField';
import { DeathCharacter } from '@shared/miscValues';
import { simpleOpenAtomFamily } from '@shared/state/atom';
import { customThemeAtom } from '@shared/state/atom/theme.atom';
import { useAuth } from '@shared/state/hooks/useAuth';
import { LowerRole } from '@shared/types';
import { formatDate } from '@shared/utils/formatDate';
import { mainBgColorGenerator } from '@shared/utils/mainColorGenerator';
import { message, Radio, Table, TableColumnsType } from 'antd';
import { RangePickerProps } from 'antd/es/date-picker';
import dayjs from 'dayjs';
import FuzzySearch from 'fuzzy-search';
import _ from 'lodash';
import { CSVLink } from 'react-csv';
import { useLocation } from 'react-router-dom';

import WorkerDetailModalOpener from '../WorkerDetailModalOpener/WorkerDetailModalOpener';
import { DeliveredImageModal } from './DeliveredImageModal';

const MAX_RANGE_DAYS = 60;

interface Props {
  flowerStatList: FlowerStatItem[];
  teamTypesFilter: CheckBoxFilter | null;
  dateType?: FlowerStatDateType;
  onDateTypeChange: (dateType: FlowerStatDateType) => void;
  onDateRangeChange: (newStart: string, newEnd: string) => void;
}

type ShopOrderTableData = {
  id: string;
  index: number;
  flowerStatItem: FlowerStatItem;
  shopItemName: string;
  workerName: string;
  workerId: string;
  funeralHomeName: string;
  deceasedName: string;
  coffinOutDate: string;
  teamName: string;
  teamType: string;
  region: string;
  requestAt: string;
  purchasedAt: string;
  priceWhole: number;
  priceRetail: number;
  priceRebateAgency: number;
  priceRebate: number;
  priceRebateWorker: number;
  deliveredImageUrl: string | null;
};

const getNetPriceWhole = (resultValue: FlowerStatItem[]) =>
  resultValue.reduce((accu, prev) => {
    return accu + prev.priceWhole;
  }, 0);

const getNetPriceAgencyIncome = (resultValue: FlowerStatItem[]) =>
  resultValue.reduce((accu, prev) => {
    return accu + (prev.priceRebateAgency ?? prev.priceRebate);
  }, 0);

const getNetPriceIncome = (resultValue: FlowerStatItem[]) =>
  resultValue.reduce((accu, prev) => {
    return accu + (prev.priceRebateWorker ?? 0);
  }, 0);

export const AgencyAdminShopOrderListTable: FC<Props> =
  function AgencyAdminShopOrderListTable({
    flowerStatList,
    teamTypesFilter,
    dateType,
    onDateTypeChange,
    onDateRangeChange: onDateRangeChangeProp,
  }: Props) {
    const customTheme = useRecoilValue(customThemeAtom);
    const location = useLocation();
    const showSettings = new URLSearchParams(location.search).get('settings') === 'true';
    const [inputValue, setInputValue] = useState<string>('');
    const [searchValue, setSearchValue] = useState<string>('');

    const [dateRange, setDateRange] = useState<[any | null, any | null]>([
      dayjs().subtract(30, 'day').startOf('day'),
      dayjs().endOf('day'),
    ]);

    //* 배송사진 모달
    const deliveredImageOpenId = 'agencyAdminShopOrderListTable-deliveredImageOpen';
    const setDeliveredImageOpen = useSetRecoilState(
      simpleOpenAtomFamily(deliveredImageOpenId),
    );
    const [selectFlowerStatItem, setSelectFlowerStatItem] = useState<
      FlowerStatItem | undefined
    >(undefined);

    const { userProfile, bugoRole, userBugoBrand } = useAuth();

    const bugoBrand =
      bugoRole() === LowerRole.BugoAgencyBranchAdmin
        ? userBugoBrand
        : userProfile?.bugoAgencyAdminDetail?.bugoBrands[0];

    const handleDateRangeChange: RangePickerProps['onChange'] = (dates) => {
      if (dates && dates[0] && dates[1]) {
        const diffDays = dayjs(dates[1]).diff(dayjs(dates[0]), 'day');
        if (diffDays > MAX_RANGE_DAYS) {
          message.warning(
            `조회 가능한 최대 기간은 ${MAX_RANGE_DAYS}일입니다. 기간을 다시 설정해주세요.`,
          );
          // 이전 값으로 강제 리렌더링하여 DatePicker UI 복원
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          setDateRange((prev) => [...prev] as [any, any]);
          return;
        }
        setDateRange(dates);
        //* 부모에게 날짜 변경 알림 → API 재호출
        onDateRangeChangeProp(
          dates[0].startOf('day').toISOString(),
          dates[1].endOf('day').toISOString(),
        );
      } else {
        setDateRange([null, null]);
      }
    };

    // TeamType Filtering
    const [selectedTeamTypes, setSelectedTeamTypes] = useState(teamTypesFilter);

    const teamTypesFilteredList = useMemo(() => {
      if (selectedTeamTypes) {
        return flowerStatList.filter((item) => selectedTeamTypes[item.teamType ?? '']);
      } else {
        return flowerStatList;
      }
    }, [flowerStatList, selectedTeamTypes]);

    const resultValue = useMemo(() => {
      const searcher = new FuzzySearch(teamTypesFilteredList, [
        'shopItemName',
        'workerName',
        'teamName',
        'teamType',
        'region',
        'funeralHomeName',
        'deceasedName',
        'senderPhrase',
      ]);
      return searcher.search(searchValue);
    }, [teamTypesFilteredList, searchValue]);

    const columns: TableColumnsType<ShopOrderTableData> = [
      {
        title: 'index',
        dataIndex: 'index',
        key: 'index',
        align: 'center',
        render: (index) => {
          return <p className="font-medium">{index}</p>;
        },
      },
      {
        title: '소속',
        dataIndex: 'teamName',
        key: 'teamName',
        align: 'center',
        render: (teamName) => {
          return <p className="font-medium">{teamName}</p>;
        },
      },
      {
        title: '소속지역',
        dataIndex: 'region',
        key: 'region',
        align: 'center',
        render: (region) => {
          return <p className="font-medium">{region}</p>;
        },
      },
      {
        title: '지도사(부고발송)',
        key: 'worker',
        align: 'center',
        render: (_, record) => {
          return (
            <WorkerDetailModalOpener
              workerName={record.workerName}
              workerId={record.workerId}
              fontSize={14}
            />
          );
        },
      },
      {
        title: '장례식장',
        dataIndex: 'funeralHomeName',
        key: 'funeralHomeName',
        align: 'center',
      },
      {
        title: '고인명',
        dataIndex: 'deceasedName',
        key: 'deceasedName',
        align: 'center',
        render: (deceasedName) => {
          return (
            <p>
              {DeathCharacter}&nbsp;
              {deceasedName}님
            </p>
          );
        },
      },
      {
        title: '발인일',
        dataIndex: 'coffinOutDate',
        key: 'coffinOutDate',
        align: 'center',
        render: (value: string) => {
          return (
            <p>
              {value
                ? formatDate(new Date(value), {
                    contains: {
                      year: false,
                      month: true,
                      date: true,
                      day: true,
                      hours: false,
                      minutes: false,
                    },
                  })
                : ''}
            </p>
          );
        },
      },
      {
        title: '상품명',
        dataIndex: 'shopItemName',
        key: 'name',
        align: 'center',
        render: (name) => {
          return <p className="font-medium">{name}</p>;
        },
      },
      {
        title: '주문일',
        dataIndex: 'requestAt',
        key: 'requestAt-date',
        align: 'center',

        render: (value: string) => {
          return (
            <p>
              {value
                ? formatDate(new Date(value), {
                    contains: {
                      year: false,
                      month: true,
                      date: true,
                      day: true,
                      hours: false,
                      minutes: false,
                    },
                  })
                : ''}
            </p>
          );
        },
      },
      {
        title: '주문시각',
        dataIndex: 'requestAt',
        key: 'requestAt-time',
        align: 'center',

        render: (value: string) => {
          return (
            <p>
              {value
                ? formatDate(new Date(value), {
                    contains: {
                      year: false,
                      month: false,
                      date: false,
                      day: false,
                      hours: true,
                      minutes: true,
                    },
                  })
                : ''}
            </p>
          );
        },
      },
      {
        title: '화환원가',
        dataIndex: 'priceWhole',
        key: 'priceWhole',
        align: 'right',
        render: (value: number) => {
          return <p className="text-gray-600">{value.toLocaleString()}원</p>;
        },
      },

      {
        title: '결제금액',
        dataIndex: 'priceRetail',
        key: 'priceRetail',
        align: 'right',
        render: (value: number) => {
          return <p className="text-gray-600">{value.toLocaleString()}원</p>;
        },
      },
      {
        title: '사진보기',
        dataIndex: 'deliveredImageUrl',
        key: 'deliveredImage',
        align: 'center',
        render: (deliveredImageUrl: string | null, record) => {
          if (deliveredImageUrl) {
            return (
              <div className="center-box">
                <Button
                  className="py-0 px-2 text-sm leading-normal shadow-sm"
                  onClick={() => {
                    setSelectFlowerStatItem(record.flowerStatItem);
                    setDeliveredImageOpen(true);
                  }}
                >
                  사진보기
                </Button>
              </div>
            );
          } else {
            return <p className="text-sm">사진준비중</p>;
          }
        },
      },
    ];

    const tableData: ShopOrderTableData[] = useMemo(() => {
      return _.map(resultValue, (item, index): ShopOrderTableData => {
        return {
          index: resultValue.length - index,
          id: item._id,
          shopItemName: item.shopItemName,
          teamName: item.teamName,
          teamType: item.teamType,
          region: item.region,
          workerName: item.workerName,
          workerId: item.workerId,
          funeralHomeName: item.funeralHomeName,
          deceasedName: item.deceasedName,
          coffinOutDate: item.coffinOutDate,
          requestAt: item.requestedAt,
          purchasedAt: item.purchasedAt,
          priceWhole: item.priceWhole,
          priceRetail: item.priceRetail,
          priceRebateAgency: item.priceRebateAgency,
          priceRebate: item.priceRebate,
          priceRebateWorker: item.priceRebateWorker,
          deliveredImageUrl: item.deliveredImageUrl,
          flowerStatItem: item,
        };
      });
    }, [resultValue]);

    const csvTableData = tableData.map((item) => {
      return {
        소속: item.teamName,
        소속지역: item.region,
        지도사: item.workerName,
        장례식장: item.funeralHomeName,
        고인명: item.deceasedName,
        발인일: item.coffinOutDate
          ? formatDate(new Date(item.coffinOutDate), {
              contains: {
                year: false,
                month: true,
                date: true,
                day: true,
                hours: false,
                minutes: false,
              },
            })
          : '',
        상품명: item.shopItemName,
        주문일: item.requestAt
          ? formatDate(new Date(item.requestAt), {
              contains: {
                year: false,
                month: true,
                date: true,
                day: true,
                hours: false,
                minutes: false,
              },
            })
          : '',
        주문시각: item.requestAt
          ? formatDate(new Date(item.requestAt), {
              contains: {
                year: false,
                month: false,
                date: false,
                day: false,
                hours: true,
                minutes: true,
              },
            })
          : '',
        화환원가: item.priceWhole,
        결제금액: item.priceRetail,
      };
    });

    const onInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(event.currentTarget.value);
    };

    const onSearch = () => {
      setSearchValue(inputValue);
    };

    const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        onSearch();
      }
    };

    return (
      <div className="space-y-4">
        <div className="flex gap-8">
          <StatPageTableHeaderBox
            left="총 원가"
            right={getNetPriceWhole(resultValue).toLocaleString() + '원'}
          />
          <StatPageTableHeaderBox
            left={`${
              bugoRole() === LowerRole.BugoAgencyBranchAdmin
                ? userProfile?.bugoAgencyBranchAdminDetail?.teamName
                : bugoBrand?.bugoAgency?.nickName
            } 이익금`}
            right={getNetPriceAgencyIncome(resultValue).toLocaleString() + '원'}
          />
          {getNetPriceIncome(resultValue) > 0 && (
            <StatPageTableHeaderBox
              left="지도사"
              right={getNetPriceIncome(resultValue).toLocaleString() + '원'}
            />
          )}
          <StatPageTableHeaderBox
            left="화환 주문량"
            right={resultValue.length.toLocaleString() + '건'}
          />
        </div>
        <div className="rounded-lg bg-white px-4">
          <div className="flex w-full items-center justify-between py-4">
            <div className="flex items-center justify-start gap-2">
              <p className="font-medium text-gray-600">
                총{' '}
                <span className="font-bold text-black">
                  {resultValue.length.toLocaleString()}
                </span>
                건
              </p>
              <div className="flex items-center justify-start gap-2">
                <TextField
                  className="border-gray-500"
                  type={'search'}
                  value={inputValue}
                  onChange={onInputChange}
                  onKeyDown={onKeyDown}
                  placeholder="검색 (지도사, 장례식장, 고인명, 소속, 상품명)"
                />
                <Button
                  className="filled-gray-800 whitespace-nowrap rounded-md border-0 px-4 py-3 text-sm font-bold shadow-none"
                  onClick={onSearch}
                >
                  <p className="text-white">검색</p>
                </Button>
              </div>
            </div>
            <div className="flex flex-col items-end gap-1">
              {showSettings && (
                <Radio.Group
                  value={dateType}
                  onChange={(e) => onDateTypeChange(e.target.value)}
                  size="small"
                  optionType="button"
                  buttonStyle="solid"
                >
                  <Radio.Button value="purchasedAt">판매일 기준</Radio.Button>
                  <Radio.Button value="coffinOutDate">발인일 기준</Radio.Button>
                </Radio.Group>
              )}
              <DateFilterSearcher
                dateRange={dateRange}
                onChange={handleDateRangeChange}
                size="large"
              />
            </div>
            {/* 소속 필터 */}
            {selectedTeamTypes && (
              <div className="flex items-center space-x-4 border text-sm theme-bg-1 theme-border-1">
                <div
                  className={`center-box self-stretch bg-opacity-40 px-2 font-bold ${mainBgColorGenerator(
                    customTheme,
                  )}`}
                >
                  소속
                </div>
                <div className="flex flex-1 items-center justify-around space-x-4 py-1 pr-2">
                  <CheckboxFilter
                    filter={selectedTeamTypes}
                    setFilter={(checked) => setSelectedTeamTypes(checked)}
                  />
                </div>
              </div>
            )}
            <CSVLink
              filename={'Expense_Table.csv'}
              data={_.flatMapDeep([csvTableData])}
              className="center-box filled-gray-800 rounded-md px-4 py-2 font-bold"
            >
              <p className="text-white">엑셀 다운로드</p>
            </CSVLink>
          </div>
          <Table
            className="w-full"
            columns={columns}
            dataSource={_.flatMapDeep([tableData])}
            pagination={{
              position: ['bottomCenter'],
              defaultPageSize: 15,
              showSizeChanger: true,
              pageSizeOptions: [10, 15, 20, 50, 100],
            }}
            rowKey={'id'}
            bordered
            size={'small'}
          />
          <DeliveredImageModal
            openId={deliveredImageOpenId}
            flowerStatItem={selectFlowerStatItem}
          />
        </div>
      </div>
    );
  };
